import { RawDraftContentState } from 'draft-js';
import { flattenDeep, isEqual, isNaN, isNull, isUndefined } from 'lodash/fp';
import Fuse from 'fuse.js';

import { DatasetFieldType } from '../../../store/types';
import { SingleInstanceField, AnnexInstanceField, CurrencyValue, Valuation } from '../../store/types';
import { Comparator, InstanceFieldTableFilter, TextFilterType } from '../../store/filterTypes';
import { isEmpty } from '../../../../shared/wysiwyg/WYSIWYG';
import { newCalculatorField } from '../../../../shared/calculator/constants';
import { isSameOrAfter, isSameOrBefore } from '../../../../../utils/luxon';

const checkNumberFilter = (value: number | undefined, numberFilter: number | undefined, comparator: Comparator | null) => {
    if (isUndefined(numberFilter)) {
        return true;
    }
    if (isUndefined(value)) {
        return false;
    }
    switch (comparator) {
        case Comparator.LESS_THAN:
            return value < numberFilter;
        case Comparator.LESS_OR_EQUAL:
            return value <= numberFilter;
        case Comparator.GREATER_THAN:
            return value > numberFilter;
        case Comparator.GREATER_OR_EQUAL:
            return value >= numberFilter;
        case Comparator.NOT_EQUAL:
            return value !== numberFilter;
        case Comparator.EQUAL:
        default:
            return value === numberFilter;
    }
};

const checkTimeFilter = (value: string, timeFilter: string | null, comparator: Comparator | null) => {
    if (isNull(timeFilter)) {
        return true;
    }
    if (value === '') {
        return false;
    }
    const [valueHoursString, valueMinutesString] = value.split(':');
    const valueHours = parseInt(valueHoursString);
    const valueMinutes = parseInt(valueMinutesString);
    const [timeFilterHoursString, timeFilterMinutesString] = timeFilter.split(':');
    const timeFilterHours = parseInt(timeFilterHoursString);
    const timeFilterMinutes = parseInt(timeFilterMinutesString);
    if (!isNaN(valueHours) && !isNaN(valueMinutes) && !isNaN(timeFilterHours) && !isNaN(timeFilterMinutes)) {
        switch (comparator) {
            case Comparator.LESS_THAN:
                return valueHours < timeFilterHours || (valueHours === timeFilterHours && valueMinutes < timeFilterMinutes);
            case Comparator.LESS_OR_EQUAL:
                return valueHours < timeFilterHours || (valueHours === timeFilterHours && valueMinutes <= timeFilterMinutes);
            case Comparator.GREATER_THAN:
                return valueHours > timeFilterHours || (valueHours === timeFilterHours && valueMinutes > timeFilterMinutes);
            case Comparator.GREATER_OR_EQUAL:
                return valueHours > timeFilterHours || (valueHours === timeFilterHours && valueMinutes >= timeFilterMinutes);
            case Comparator.NOT_EQUAL:
                return value !== timeFilter;
            case Comparator.EQUAL:
            default:
                return value === timeFilter;
        }
    }
    return true;
};

const checkDropdownValue = (value: string | string[] | null, filter: string[] | null) => isNull(filter) || (!isNull(value) && filter.some(dropdownFilterValue => flattenDeep([value]).includes(dropdownFilterValue)));

const checkTextFilter = (value: string, textFilter: TextFilterType) => {
    if (isNull(textFilter.value)) {
        return true;
    }
    if (!textFilter.isFuzzyMatchFilter) {
        return value.toLowerCase().includes(textFilter.value.toLowerCase());
    }
    const fuse = new Fuse([value], { threshold: 0.5 });
    const fuzzyMatches = fuse.search(textFilter.value);
    return fuzzyMatches.length > 0;
};

export const checkTableCellMatchesFilter = (cell: SingleInstanceField | AnnexInstanceField, cellFilter: InstanceFieldTableFilter) => {
    const { filter, type } = cellFilter;
    switch (type) {
        case DatasetFieldType.DROPDOWN:
            return checkDropdownValue(cell.value as string[] | null, filter);
        case DatasetFieldType.CHECKBOX: {
            const checkboxValue = cell.value as boolean;
            return (filter && checkboxValue) || (!filter && !checkboxValue);
        }
        case DatasetFieldType.LONGTEXT:
        case DatasetFieldType.TEXT:
            return !isNull(cell.value) && !isUndefined(cell.value) && (cell.value as string).length > 0 && checkTextFilter(cell.value as string, filter);
        case DatasetFieldType.WYSIWYG:
            return !isNull(cell.value) && !isEmpty((cell.value as RawDraftContentState)) && checkTextFilter((cell.value as RawDraftContentState).blocks.map(({ text }) => text.toLowerCase()).join(' '), filter);
        case DatasetFieldType.TIME:
            return (isNull(filter.value) && isNull(filter.city)) || (!isNull(cell.value) && !isUndefined(cell.value) && (cell.value as string[]).every(value => value.length > 0) && (isNull(filter.city) || filter.city.includes((cell.value as string[])[1])) && checkTimeFilter((cell.value as string[])[0], filter.value, filter.comparator));
        case DatasetFieldType.NUMBER:
            return !isNull(cell.value) && !isUndefined(cell.value) && checkNumberFilter(cell.value as number, filter.value, filter.comparator);
        case DatasetFieldType.CURRENCY_AMOUNT:
            return !isNull(cell.value) && !isUndefined(cell.value) && checkNumberFilter((cell.value as CurrencyValue).value, filter.value, filter.comparator) && checkDropdownValue((cell.value as CurrencyValue).currency, filter.currency);
        case DatasetFieldType.VALUATION_PERCENTAGE: {
            const valuationPercentageValue = cell.value as Valuation[];
            return (filter.containsMaturity && valuationPercentageValue.some(valuationEntry => !isEqual(valuationEntry.maturity, [newCalculatorField()]))) || (!isUndefined(filter.containsMaturity) && !filter.containsMaturity && valuationPercentageValue.every(valuationEntry => isEqual(valuationEntry.maturity, [newCalculatorField()]))) || (isUndefined(filter.containsMaturity) && valuationPercentageValue.some(valuationEntry => checkNumberFilter(valuationEntry.percentage, filter.percentage, filter.comparator)));
        }
        case DatasetFieldType.DATE:
            return !isNull(cell.value) && ((isNull(filter.from) && !isNull(filter.to) && isSameOrBefore(cell.value as string, filter.to)) || (isNull(filter.to) && !isNull(filter.from) && isSameOrAfter(cell.value as string, filter.from)) || (!isNull(filter.to) && !isNull(filter.from) && isSameOrAfter(cell.value as string, filter.from) && isSameOrBefore(cell.value as string, filter.to)));
        default:
            return true;
    }
};
